'use client';

import { ChangeEvent, useState } from 'react';
import { Select } from '../../atoms/Inputs/Select';
import { repairCostOptions, systemAgeOptions } from './optionsData';
import { Button } from '../../atoms/Button';
import { useSearchParams } from 'next/navigation';
import { IconTools } from '@/components/icons';

export interface RepairReplaceCalculatorProps {
	testId?: string;
}

const RepairReplaceCalculator = (props: RepairReplaceCalculatorProps) => {
	const { testId = 'RepairReplaceCalculator' } = props;

	const params = useSearchParams();
	const systemAgeParam = params.get('systemAge');
	const repairCostParam = params.get('repairCost');

	// prevents user from selecting values that are not in the options by
	// checking if the query params are in the options, otherwise it will
	// default to an empty string. This prevents the user from navigating
	// to results with invalid values in the query params
	const getInitialValue = (param: string | null, options: string[]) =>
		param && options.includes(param) ? param : '';

	const [formData, setFormData] = useState({
		systemAge: getInitialValue(systemAgeParam, systemAgeOptions),
		repairCost: getInitialValue(repairCostParam, repairCostOptions),
	});

	const [ageErrorText, setAgeErrorText] = useState('');
	const [costErrorText, setCostErrorText] = useState('');

	const { systemAge, repairCost } = formData;

	const missingSystemAge = !systemAge;
	const missingRepairCost = !repairCost;
	const missingInfo = missingRepairCost || missingSystemAge;

	const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
		if (e.target.name === 'systemAge') {
			setAgeErrorText('');
		}
		if (e.target.name === 'repairCost') {
			setCostErrorText('');
		}
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		if (missingSystemAge) {
			setAgeErrorText('Select system age');
		}
		if (missingRepairCost) {
			setCostErrorText('Select repair cost');
		}
		if (missingInfo) {
			event.preventDefault();
		}
	};

	return (
		<div
			data-testid={testId}
			className="mx-auto mb-10 max-w-101 overflow-hidden rounded-2xl border-2 border-sky-600
				bg-sky-50 px-4 pb-8 pt-6 lg:mb-20 lg:px-6 lg:pb-10 lg:pt-8"
		>
			<div className="mb-6 flex justify-between lg:mb-8">
				<div className="text-sky-700">
					<div className="text-xl font-semibold lg:text-2xl">
						Repair or replace
					</div>
					<div className="text-sm font-semibold lg:text-base">Calculator</div>
				</div>
				<IconTools className="w-10 fill-sky-700 lg:w-12" />
			</div>
			<form
				className="flex flex-col"
				onSubmit={handleSubmit}
				action="/tools/repair-replace-calculator/results/"
			>
				<div className="mb-4">
					<Select
						id="systemAge"
						label="Age of system"
						name="systemAge"
						onChange={handleChange}
						value={systemAge}
						inputProps={{ className: '!bg-white' }}
						contextRowProps={{ 'data-testid': 'systemAgeContextRow' }}
						error={ageErrorText}
					>
						<option></option>
						{systemAgeOptions.map((option, i) => (
							<option key={option} value={option}>
								{`${option}${
									i === 0
										? ' year'
										: i + 1 === systemAgeOptions.length
										? '+ years'
										: ' years'
								}`}
							</option>
						))}
					</Select>
				</div>
				<div className="mb-6 lg:mb-8">
					<Select
						id="repairCost"
						label="Estimated cost of repair"
						name="repairCost"
						onChange={handleChange}
						value={repairCost}
						inputProps={{ className: '!bg-white' }}
						contextRowProps={{ 'data-testid': 'repairCostContextRow' }}
						error={costErrorText}
					>
						<option></option>
						{repairCostOptions.map((option, i) => (
							<option key={option} value={option}>
								{i + 1 === repairCostOptions.length
									? `$${option}+`
									: `$${option}`}
							</option>
						))}
					</Select>
				</div>
				<Button variant="large-default">Get my results</Button>
			</form>
		</div>
	);
};

export default RepairReplaceCalculator;
